/* Variant truth for Instant quick views. Uses server-rendered Shopify variants.
 * Collection sections cannot reliably re-render in a product-page context.
 * Scope the fix to marked quick views; leave regular product forms untouched.
 */
(() => {
  if (window.__dhQuickViewSku) return;
  window.__dhQuickViewSku = true;
  const bound = new WeakSet();
  let active = null;
  function setExpanded(root, name, expanded) {
    root.querySelectorAll('[data-overlay-open]').forEach(button => {
      if (button.getAttribute('data-overlay-open') === name) {
        button.setAttribute('aria-expanded', String(expanded));
      }
    });
  }
  function closeQuickView(restoreFocus = true) {
    if (!active) return;
    const { panel, root, trigger, overflow } = active;
    active = null;
    panel.dataset.state = 'closed';
    setExpanded(root, panel.dataset.overlay, false);
    overflow.forEach(({ element, value, priority }) => {
      if (value) element.style.setProperty('overflow', value, priority);
      else element.style.removeProperty('overflow');
    });
    if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
  }
  function focusable(panel) {
    return [...panel.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')]
      .filter(element => !element.disabled && element.tabIndex >= 0 &&
        element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
  }
  function bind(root) {
    if (bound.has(root)) return;
    bound.add(root);
    // Instant snapshots its overlay map before Load more. Resolve the panel
    // from the current DOM instead, and own only our marked quick views.
    root.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest('[data-overlay-open]');
      if (trigger) {
        const name = trigger.getAttribute('data-overlay-open');
        const panel = [...root.querySelectorAll('[data-overlay][role="dialog"]')]
          .find(element => element.dataset.overlay === name &&
            element.querySelector('[data-dh-quick-view-variants]'));
        if (!panel) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (active?.panel === panel) {
          closeQuickView();
          return;
        }
        closeQuickView(false);
        const overflow = [document.documentElement, document.body].map(element => ({
          element,
          value: element.style.getPropertyValue('overflow'),
          priority: element.style.getPropertyPriority('overflow'),
        }));
        active = { panel, root, trigger, overflow };
        overflow.forEach(({ element }) => element.style.setProperty('overflow', 'hidden'));
        panel.dataset.state = 'open';
        panel.tabIndex = -1;
        setExpanded(root, name, true);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (active?.panel === panel) {
            (focusable(panel)[0] || panel).focus({ preventScroll: true });
          }
        }));
        return;
      }
      const closer = event.target.closest('[data-overlay-close]');
      if (closer && active?.panel.contains(closer)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeQuickView();
      }
    }, true);
    root.addEventListener('keydown', event => {
      if (!active || active.root !== root) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeQuickView();
      } else if (event.key === 'Tab') {
        const elements = focusable(active.panel);
        const first = elements[0] || active.panel;
        const last = elements[elements.length - 1] || active.panel;
        const focused = root.activeElement;
        if (!active.panel.contains(focused) || (event.shiftKey ? focused === first : focused === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }
    }, true);
    new MutationObserver(() => {
      if (active?.root === root && !active.panel.isConnected) closeQuickView(false);
    }).observe(root, { childList: true, subtree: true });
    root.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('input[data-option-value-id]')) return;
      const panel = input.closest('[data-overlay][role="dialog"]');
      const data = panel?.querySelector('[data-dh-quick-view-variants]');
      const form = input.closest('form');
      if (!data || !form) return;
      let variants;
      try { variants = JSON.parse(data.textContent); } catch { return; }
      const options = [...form.querySelectorAll('fieldset')]
        .map(field => field.querySelector('input[data-option-value-id]:checked'))
        .filter(Boolean)
        .map(option => option.value);
      const variant = variants.find(item =>
        item.options.length === options.length && item.options.every((value, i) => value === options[i])
      );
      // Prevent Instant's collection-section request from overwriting this
      // resolved variant with the first variant from an unrelated render.
      event.stopImmediatePropagation();
      const id = form.querySelector('input[name="id"]');
      if (id) {
        id.value = variant?.id || '';
        id.setAttribute('value', id.value);
      }
      const sku = panel.querySelector('[data-dh-sku-value]');
      if (sku) sku.textContent = variant ? (variant.sku || 'Not assigned') : 'Unavailable combination';
      const priceRegion = data.parentElement;
      const prices = priceRegion.querySelector('div');
      if (prices && variant) {
        const primary = prices.querySelector('span');
        if (primary) primary.textContent = variant.price;
        let compare = prices.querySelector('span + span');
        if (variant.compare) {
          if (!compare) {
            compare = document.createElement('span');
            compare.style.cssText = 'font-size:14px;opacity:.45;text-decoration:line-through';
            prices.append(compare);
          }
          compare.textContent = variant.compare;
        } else {
          compare?.remove();
        }
      }
      const stock = priceRegion.querySelector('p:not([data-dh-quick-view-sku])');
      if (stock) stock.textContent = !variant ? 'Unavailable combination' : variant.available ? 'In stock' : 'Currently sold out';
      form.querySelectorAll('button[type="submit"]').forEach(button => {
        button.disabled = !variant?.available;
      });
      const image = panel.querySelector('[data-product-update*="media"] img');
      if (image && variant?.image) {
        image.src = variant.image;
        image.removeAttribute('srcset');
      }
    }, true);
  }
  function scan() {
    if (active && !active.panel.isConnected) closeQuickView(false);
    document.querySelectorAll('instant-layout').forEach(host => {
      if (host.shadowRoot) bind(host.shadowRoot);
    });
  }
  scan();
  document.addEventListener('shopify:section:load', scan);
  new MutationObserver(scan).observe(document.documentElement, {childList: true, subtree: true});
})();
